import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2, Phone, Mail, MapPin, Search, History } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { ClientTimeline } from "@/components/studio/ClientTimeline";
import { studio, apiError, inputCls, btnPrimary, btnGhost } from "@/lib/studioApi";
import { ListSkeleton } from "@/components/studio/StudioUI";

const empty = { name: "", company: "", phone: "", email: "", address: "", postal_code: "", city: "", notes: "", client_type: "particulier", vat: "" };

export default function StudioClients() {
  const [clients, setClients] = useState(null);
  const [form, setForm] = useState(empty);
  const [params] = useSearchParams();
  const [showForm, setShowForm] = useState(params.get("nouveau") === "1");
  const [looking, setLooking] = useState(false);
  const [search, setSearch] = useState("");
  const [timeline, setTimeline] = useState(null);
  const [filter, setFilter] = useState("tous");

  const query = search.trim().toLowerCase();
  const visible = (clients || []).filter((c) => {
    if (filter !== "tous" && (c.client_type || "particulier") !== filter) return false;
    if (!query) return true;
    return [c.name, c.company, c.city, c.vat, c.email, c.phone, c.postal_code]
      .some((v) => (v || "").toLowerCase().includes(query));
  });

  const load = useCallback(() => {
    studio.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await studio.post("/clients", form);
      toast.success("Client enregistré.");
      setForm(empty);
      setShowForm(false);
      load();
    } catch (e) { toast.error(apiError(e, "Enregistrement impossible.")); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer ${c.name} ?`)) return;
    try {
      await studio.delete(`/clients/${c.id}`);
      load();
    } catch (e) { toast.error("Suppression impossible."); }
  };

  const lookupVat = async () => {
    setLooking(true);
    try {
      const { data } = await studio.get(`/vat-lookup?vat=${encodeURIComponent(form.vat)}`);
      if (!data.valid) {
        toast.error("Ce numéro de TVA n'est pas actif dans la base européenne.");
        return;
      }
      setForm((f) => ({
        ...f,
        client_type: "professionnel",
        vat: data.vat,
        company: data.name || f.company,
        name: f.name || "",
        address: data.address || f.address,
        postal_code: data.postal_code || f.postal_code,
        city: data.city || f.city,
      }));
      toast.success("Société trouvée. Complétez le nom du contact.");
    } catch (e) { toast.error(apiError(e, "Recherche impossible.")); }
    finally { setLooking(false); }
  };

  return (
    <StudioLayout
      title="Clients"
      subtitle="Votre carnet d'adresses : réutilisez un client en un tap lors de la création d'un dossier."
      action={<button type="button" data-testid="new-client-btn" onClick={() => setShowForm(!showForm)} className={btnPrimary}><Plus className="w-4 h-4" /> Nouveau client</button>}
    >
      {showForm && (
        <div data-testid="client-form" className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)] space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 flex flex-wrap gap-2">
              {[["particulier", "Client particulier"], ["professionnel", "Client professionnel"]].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  data-testid={`client-type-${value}`}
                  onClick={() => setForm({ ...form, client_type: value })}
                  className={`rounded-xl border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                    (form.client_type || "particulier") === value
                      ? "border-[#3A5CC8] bg-[#EEF1FC] text-[#3A5CC8]"
                      : "border-[#E2E7F7] bg-white text-[#5C6A87] hover:border-[#E2E7F7]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {form.client_type === "professionnel" && (
              <div className="sm:col-span-2 flex flex-col sm:flex-row gap-2">
                <input data-testid="client-vat" className={inputCls} placeholder="N° de TVA (ex. BE0123456789)" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} />
                <button type="button" data-testid="client-vat-lookup" disabled={looking || form.vat.trim().length < 8} onClick={lookupVat} className={`${btnGhost} whitespace-nowrap`}>
                  <Search className="w-3.5 h-3.5" /> {looking ? "Recherche…" : "Rechercher la société"}
                </button>
              </div>
            )}
            <input data-testid="client-name" className={inputCls} placeholder="Nom *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="client-company" className={inputCls} placeholder="Société (facultatif)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            <input data-testid="client-phone" className={inputCls} placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <input data-testid="client-email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="client-address" className={inputCls} placeholder="Adresse" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <input data-testid="client-postal" className={inputCls} placeholder="Code postal" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
            <input data-testid="client-city" className={inputCls} placeholder="Commune" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <button type="button" data-testid="client-save-btn" disabled={!form.name.trim()} onClick={create} className={btnPrimary}>Enregistrer</button>
            <button type="button" onClick={() => setShowForm(false)} className={btnGhost}>Annuler</button>
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#93A0BC]" />
          <input
            data-testid="client-search"
            className={`${inputCls} pl-9`}
            placeholder="Rechercher : initiales, nom, société, ville, TVA…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {[["tous", "Tous"], ["particulier", "Particuliers"], ["professionnel", "Professionnels"]].map(([v, l]) => (
            <button
              key={v}
              type="button"
              data-testid={`client-filter-${v}`}
              onClick={() => setFilter(v)}
              className={`rounded-xl border px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${
                filter === v ? "border-[#3A5CC8] bg-[#EEF1FC] text-[#3A5CC8]" : "border-[#E2E7F7] bg-white text-[#5C6A87] hover:border-[#E2E7F7]"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {clients === null && <ListSkeleton />}
        {clients !== null && !visible.length && (
          <p className="text-sm text-[#5C6A87]">{clients.length ? "Aucun client ne correspond à votre recherche." : "Aucun client enregistré."}</p>
        )}
        {visible.map((c) => (
          <article key={c.id} data-testid={`client-card-${c.id}`} className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-heading text-base font-semibold text-[#0F2645]">{c.name || c.company}</p>
                {c.company && c.company !== c.name && <p className="text-xs text-[#5C6A87]">{c.company}</p>}
                <p className="mt-1 inline-flex flex-wrap items-center gap-2 text-[11px] font-semibold">
                  <span className={`rounded-full px-2 py-0.5 ${c.client_type === "professionnel" ? "bg-[#EEF1FC] text-[#3A5CC8]" : "bg-slate-100 text-[#5C6A87]"}`}>
                    {c.client_type === "professionnel" ? "Professionnel" : "Particulier"}
                  </span>
                  {c.vat && <span className="text-[#5C6A87]">TVA {c.vat}</span>}
                </p>
              </div>
              <span className="flex items-center gap-2">
                <button type="button" data-testid={`client-timeline-${c.id}`} onClick={() => setTimeline(c)} className="inline-flex items-center gap-1 rounded-full border border-[#E2E7F7] px-2.5 py-1 text-xs font-semibold text-[#3A5CC8] hover:border-[#3A5CC8]"><History className="w-3.5 h-3.5" /> Suivi</button>
                <button type="button" data-testid={`client-delete-${c.id}`} onClick={() => remove(c)} className="text-[#93A0BC] hover:text-red-600 transition-colors" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
              </span>
            </div>
            <div className="mt-3 space-y-1 text-sm text-[#5C6A87]">
              {c.phone && <p className="inline-flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#3A5CC8]" /> {c.phone}</p>}
              {c.email && <p className="inline-flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#3A5CC8]" /> {c.email}</p>}
              {(c.address || c.city) && <p className="inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#3A5CC8]" /> {c.address} {c.postal_code} {c.city}</p>}
            </div>
          </article>
        ))}
      </div>
      {timeline && <ClientTimeline client={timeline} onClose={() => setTimeline(null)} />}
    </StudioLayout>
  );
}
